import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckSquare, Plus, Trash2, Circle, CheckCircle2, CalendarClock, User } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", due_date: "" });
  const [mineOnly, setMineOnly] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const [t, m] = await Promise.all([api.get("/staff/tasks"), api.get("/staff/members")]);
      setTasks(t.data); setMembers(m.data);
    } catch { /* */ }
  }

  async function add() {
    if (!form.title.trim() || !form.assignee_id) return toast.error("Add a title and pick an assignee.");
    try { await api.post("/staff/tasks", { title: form.title.trim(), description: form.description, assignee_id: form.assignee_id, due_date: form.due_date }); setForm({ title: "", description: "", assignee_id: "", due_date: "" }); toast.success("Task created."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function toggle(t) {
    try { await api.patch(`/staff/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }); load(); }
    catch { toast.error("Failed."); }
  }
  async function del(id) {
    try { await api.delete(`/staff/tasks/${id}`); toast.success("Task deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not allowed."); }
  }

  const sortByDue = (a, b) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  };
  const visible = mineOnly ? tasks.filter((t) => t.assignee_id === user?.id) : tasks;
  const open = visible.filter((t) => t.status !== "done").sort(sortByDue);
  const done = visible.filter((t) => t.status === "done");

  function Row({ t }) {
    const canDelete = t.created_by === user?.id || isAdmin;
    const overdue = t.due_date && t.status !== "done" && t.due_date < today;
    return (
      <div className={`flex items-start gap-3 rounded-xl border bg-card p-4 ${overdue ? "border-destructive/50 ring-1 ring-destructive/20" : "border-border"}`} data-testid={`task-${t.id}`}>
        <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="mt-0.5 text-primary">
          {t.status === "done" ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
        </button>
        <div className="flex-1">
          <p className={`font-medium ${t.status === "done" ? "text-muted-foreground line-through" : ""}`}>{t.title}</p>
          {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Assigned to <span className="font-medium text-foreground">{t.assignee_name}</span></span>
            <Badge variant="outline" className="text-[10px]">{t.assignee_role}</Badge>
            {t.due_date && (
              <span className={`inline-flex items-center gap-1 ${overdue ? "font-semibold text-destructive" : ""}`} data-testid={`task-due-${t.id}`}>
                <CalendarClock className="h-3 w-3" /> {t.due_date}{overdue ? " · overdue" : ""}
              </span>
            )}
            <span>· by {t.created_by_name}</span>
          </div>
        </div>
        {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(t.id)} data-testid={`task-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><CheckSquare className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">Tasks</h1>
          <p className="text-sm text-muted-foreground">Shared task list. Assign any teammate — everyone on staff can see all tasks.</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_auto]">
          <Input placeholder="Task title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
          <Input placeholder="Details (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" />
          <Input type="date" title="Due date (optional)" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" />
          <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
            <SelectTrigger className="min-w-[160px]" data-testid="task-assignee"><SelectValue placeholder="Assign to..." /></SelectTrigger>
            <SelectContent>
              {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-2 flex justify-end">
          <Button className="rounded-full" onClick={add} data-testid="task-add-btn"><Plus className="mr-1 h-4 w-4" /> Add task</Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant={mineOnly ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setMineOnly((v) => !v)} data-testid="task-mine-filter">
          <User className="mr-1 h-4 w-4" /> {mineOnly ? "Showing: assigned to me" : "Assigned to me"}
        </Button>
        {mineOnly && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setMineOnly(false)} data-testid="task-mine-clear">Show all</Button>}
      </div>

      <div>
        <h2 className="mb-3 font-heading text-lg font-bold">Open ({open.length})</h2>
        {open.length === 0 ? <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No open tasks.</p>
          : <div className="space-y-2">{open.map((t) => <Row key={t.id} t={t} />)}</div>}
      </div>

      {done.length > 0 && (
        <div>
          <h2 className="mb-3 font-heading text-lg font-bold">Completed ({done.length})</h2>
          <div className="space-y-2">{done.map((t) => <Row key={t.id} t={t} />)}</div>
        </div>
      )}
    </div>
  );
}
